import clsx from "clsx";
import type * as React from "react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { ZodArray, type ZodEnum } from "zod";

import { camelToTitle } from "~/utils/helpers";

interface FormSelectProps {
	formKey: string;
	schema: ZodEnum | ZodArray<ZodEnum>;
}

const FormSelect: React.FC<FormSelectProps> = ({ formKey, schema }) => {
	const {
		register,
		formState: { errors },
		setValue,
		watch,
	} = useFormContext();

	const isMultiSelect = schema instanceof ZodArray;

	const options = (
		!isMultiSelect ? schema.options : schema.def.element.options
	).map((value) => ({
		value,
		label: camelToTitle(value as string),
	}));

	const selectedValue = watch(formKey);

	const defaultValue = schema.meta()?.defaultValue;

	useEffect(() => {
		if (watch(formKey)) return;

		if (defaultValue) {
			setTimeout(() => {
				setValue(formKey, defaultValue);
			}, 0);
		} else if (isMultiSelect) {
			setValue(formKey, []);
		} else {
			setValue(formKey, undefined);
		}
	}, [defaultValue, formKey, isMultiSelect, setValue, watch]);

	return (
		<div className="relative flex flex-wrap gap-2">
			{options.map((option) => (
				<label
					key={option.value as string}
					className={clsx(
						"flex cursor-pointer select-none items-center gap-3 rounded-full border bg-white laptop:px-3.5 px-3 laptop:py-1.5 py-1.5 shadow shadow-slate-200 transition-all duration-150 hover:border-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:shadow-slate-900 dark:hover:border-slate-600 dark:hover:bg-sky-300/10",

						errors[formKey as keyof typeof errors],

						{
							"border-sky-600/70 bg-sky-100! text-sky-900 shadow shadow-slate-200 dark:border-sky-300/50 dark:bg-sky-300/20! dark:text-sky-200 dark:shadow-slate-900":
								(isMultiSelect && selectedValue?.includes?.(option.value)) ||
								selectedValue === option.value,
						},
					)}
				>
					<input
						type={isMultiSelect ? "checkbox" : "radio"}
						hidden
						value={option.value as string}
						{...register(formKey)}
						onClick={(_e) => {
							if (!isMultiSelect && selectedValue === option.value) {
								setValue(formKey, undefined);
							}
						}}
					/>

					<span className="text-base capitalize">{option.label}</span>
				</label>
			))}
		</div>
	);
};

export default FormSelect;
